import React from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { GetEntitlementsService } from "@application/useCases/workspace/GetEntitlementsService";
import type { Entitlement } from "@domain/schemas/BI";

const entitlementsState: { cache: Entitlement[] | null; inFlight: Promise<Entitlement[]> | null } = {
  cache: null,
  inFlight: null,
};

export interface EntitlementsQuery {
  entitlements: Entitlement[] | null;
  error: string | null;
  isLoading: boolean;
  refresh: () => void;
}

/**
Shared so several locked badges/pages mounting at once issue a single request.
*/
async function fetchEntitlementsCached(service: GetEntitlementsService): Promise<Entitlement[]> {
  if (entitlementsState.cache) {
    return entitlementsState.cache;
  }
  if (entitlementsState.inFlight) {
    return entitlementsState.inFlight;
  }

  entitlementsState.inFlight = (async () => {
    try {
      const entitlements = await service.getEntitlementsAsync();
      entitlementsState.cache = entitlements;
      return entitlements;
    } finally {
      entitlementsState.inFlight = null;
    }
  })();

  return entitlementsState.inFlight;
}

/**
Clears the cache so the next consumer re-reads the backend's licensed feature state.
*/
export function invalidateEntitlementsCache(): void {
  entitlementsState.cache = null;
}

/**
The deployment's licensed feature state (`GET /entitlements`), or `null` while still loading.

Lets a locked badge/message name *which* entitlement is missing instead of a bare "Locked" label.
*/
export function useEntitlements(): EntitlementsQuery {
  const service = useInjection(GetEntitlementsService);
  const [entitlements, setEntitlements] = React.useState<Entitlement[] | null>(entitlementsState.cache);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(entitlementsState.cache === null);
  const [reloadVersion, setReloadVersion] = React.useState(0);

  React.useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchEntitlementsCached(service);
        if (!isCancelled) {
          setEntitlements(result);
          setIsLoading(false);
        }
      } catch (loadError) {
        if (!isCancelled) {
          setError(loadError instanceof Error ? loadError.message : String(loadError));
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      isCancelled = true;
    };
  }, [service, reloadVersion]);

  const refresh = React.useCallback(() => {
    invalidateEntitlementsCache();
    setReloadVersion((version) => version + 1);
  }, []);

  return { entitlements, error, isLoading, refresh };
}

/**
Looks up a single named entitlement's active state. `null` (still loading) is treated as not yet
known - callers deciding whether to render a locked badge already have that information from
elsewhere (the 403 they received), this hook only supplies the name/active pair to display.
*/
export function useIsEntitled(name: string): boolean | null {
  const { entitlements, isLoading } = useEntitlements();
  return isLoading || !entitlements ? null : (entitlements.find((entitlement) => entitlement.name === name)?.isActive ?? false);
}
